import React, { useEffect, useReducer, useRef, useState } from 'react'
import PropTypes, {
	string,
	shape,
	object,
	func,
	bool,
	number,
} from 'prop-types'
import Axios from 'axios'
import {
	errorMessage,
	INITIAL_STATE,
	optionsReducer,
	populateData,
} from './utils/searchReducer'
import { ACTION_TYPES } from './utils/searchActionTypes'
import SearchSelect from './searchSelect'
import { useFocusCallback } from 'utils/fieldHooks'
import { Form, Input } from 'antd'
import { Auth } from 'aws-amplify'

const DatabaseField = ({
	pageFieldsData,
	className,
	onChange,
	onFocus,
	inputValue,
	form,
	disabled,
	visible,
	first,
}) => {
	const { variable, id, remove_autocomplete } = pageFieldsData

	const isObj = typeof variable === 'object'
	const varname = isObj ? variable.name : variable
	const name = id !== undefined ? `${varname}_${id}` : varname
	const hasAutocomplete = hasApiAutocomplete(remove_autocomplete)

	const waitTimeout = useRef()

	const focusCallback = useFocusCallback(onFocus, pageFieldsData)

	const [state, dispatch] = useReducer(optionsReducer, INITIAL_STATE)

	const props = {
		inputValue,
		form,
		name,
		onChange,
		waitTimeout,
		disabled,
		dispatch,
		pageFieldsData,
		first,
		className,
		focusCallback,
		visible,
		state,
	}
	if (hasAutocomplete) {
		return <WithAutocomplete {...props} />
	}

	return <WithoutAutocomplete {...props} />
}

DatabaseField.propTypes = {
	pageFieldsData: shape({
		label: string.isRequired,
		variable: object.isRequired,
		type: string.isRequired,
		info: string,
	}).isRequired,
	form: object,
	className: string,
	onChange: func,
	onFocus: func,
	inputValue: PropTypes.oneOfType([number, string, object]),
	disabled: bool,
	visible: bool,
	first: bool,
}

DatabaseField.defaultProps = {
	visible: true,
}

export default DatabaseField

function hasApiAutocomplete(removeAutocomplete) {
	if (removeAutocomplete === undefined) {
		return true
	}

	if (removeAutocomplete !== undefined && removeAutocomplete === false) {
		return true
	}

	return false
}

const WithAutocomplete = ({
	inputValue,
	form,
	name,
	onChange,
	waitTimeout,
	disabled,
	dispatch,
	pageFieldsData,
	className,
	focusCallback,
	visible,
	state,
}) => {
	const { variable } = pageFieldsData

	const [dataSearch, setDataSearch] = useState('')
	const [labelValue, setLabelValue] = useState(
		typeof inputValue === 'object' ? inputValue.exibicao : ''
	)
	const loadedOneTime = useRef(false)

	const url = variable?.database_endpoint
	const search_key = variable?.search_key
	const display_key = variable?.display_key

	useEffect(() => {
		const cancelToken = Axios.CancelToken.source()

		async function axiosRequestIntegration() {
			const session = await Auth.currentSession()
			const accessToken = session.getIdToken()
			const jwtToken = accessToken.getJwtToken()

			if (dataSearch.length >= 3) {
				dispatch({ type: ACTION_TYPES.FETCH_START })
				Axios.get(`${url}?search=${dataSearch}`, {
					cancelToken: cancelToken.token,
					headers: {
						Authorization: `Bearer ${jwtToken}`,
					},
				})
					.then((response) => {
						const data = populateData(response, display_key, search_key)
						dispatch({ type: ACTION_TYPES.FETCH_SUCCESS, payload: data })
					})
					.catch((error) => {
						dispatch({ type: ACTION_TYPES.FETCH_ERROR })
						errorMessage(Axios, error)
					})
			} else {
				dispatch({ type: ACTION_TYPES.FETCH_CLEAR })
			}
		}
		axiosRequestIntegration()

		return () => {
			cancelToken.cancel()
		}
	}, [dataSearch, url, search_key, display_key, dispatch])

	const handleSearch = (e) => {
		clearTimeout(waitTimeout.current)

		waitTimeout.current = setTimeout(() => {
			setDataSearch(e)
		}, 500)
	}

	// make an array of one option to input with informations from inputValue
	useEffect(() => {
		if (
			inputValue !== '' &&
			!loadedOneTime.current &&
			typeof inputValue === 'object'
		) {
			loadedOneTime.current = true
			dispatch({
				type: ACTION_TYPES.FETCH_SUCCESS,
				payload: [{ label: inputValue.exibicao, value: inputValue.indice }],
			})
		}
	}, [inputValue, dispatch])

	useEffect(() => {
		if (form) {
			form.setFieldsValue({
				[name]: { ...form.getFieldsValue()[name], EXIBICAO: labelValue },
			})
		}
	}, [form, name, labelValue])

	const handleChange = (e) => {
		let label = state.options[0].label
		if (state.options.length > 1) {
			label = state.options.find((option) => option.value === e).label
		}
		setLabelValue(label)
		onChange(e)
	}

	return (
		<>
			<Form.Item
				style={{ display: 'none' }}
				name={[name, 'EXIBICAO']}
				initialValue={
					typeof inputValue === 'object' ? inputValue.exibicao : ''
				}>
				<Input />
			</Form.Item>
			<SearchSelect
				pageFieldsData={{
					...pageFieldsData,
					options: state.options,
					list: name,
				}}
				state={state}
				onSearch={handleSearch}
				onFocus={focusCallback}
				onChange={handleChange}
				{...{ className, inputValue, disabled, visible, form }}
			/>
		</>
	)
}

const WithoutAutocomplete = ({
	inputValue,
	form,
	name,
	onChange,
	waitTimeout,
	disabled,
	dispatch,
	pageFieldsData,
	first,
	className,
	focusCallback,
	visible,
	state,
}) => {
	const { variable } = pageFieldsData
	const { loading, error, options } = state

	const url = variable?.database_endpoint
	const search_key = variable?.search_key
	const display_key = variable?.display_key

	const [exibicaoValue, setExibicaoValue] = useState(
		typeof inputValue === 'object' ? inputValue.exibicao : ''
	)

	const handleChange = (e) => {
		const value = e.target.value
		clearTimeout(waitTimeout.current)

		waitTimeout.current = setTimeout(() => {
			setExibicaoValue(value)
			if (!loading) dispatch({ type: ACTION_TYPES.FETCH_START })
		}, 400)
	}

	useEffect(() => {
		if (form) {
			if (error && !options) {
				form.setFieldsValue({
					[name]: { ...form.getFieldsValue()[name], INDICE: undefined },
				})
			} else {
				form.setFieldsValue({
					[name]: { ...form.getFieldsValue()[name], INDICE: options.value },
				})
			}
		}
	}, [form, name, error, options])

	useEffect(() => {
		const cancelToken = Axios.CancelToken.source()

		async function axiosRequestIntegration() {
			const session = await Auth.currentSession()
			const accessToken = session.getIdToken()
			const jwtToken = accessToken.getJwtToken()

			if (exibicaoValue.length >= 3) {
				dispatch({ type: ACTION_TYPES.FETCH_START })
				Axios.get(`${url}?search=${exibicaoValue}`, {
					cancelToken: cancelToken.token,
					headers: {
						Authorization: `Bearer ${jwtToken}`,
					},
				})
					.then((response) => {
						const searchResult = response.data

						if (searchResult.length > 0) {
							const data = {
								label: searchResult[0][display_key],
								value: searchResult[0][search_key],
							}
							onChange(data.label, 'INDICE')
							dispatch({ type: ACTION_TYPES.FETCH_SUCCESS, payload: data })
						} else {
							dispatch({ type: ACTION_TYPES.FETCH_ERROR })
						}
					})
					.catch((error) => {
						dispatch({ type: ACTION_TYPES.FETCH_ERROR })
						errorMessage(Axios, error)
					})
			} else {
				dispatch({ type: ACTION_TYPES.FETCH_CLEAR })
			}
		}

		axiosRequestIntegration()

		return () => {
			cancelToken.cancel()
		}
	}, [exibicaoValue, url, display_key, search_key, dispatch, onChange])

	return (
		<>
			<Form.Item
				key={'EXIBICAO ' + name}
				name={[name, 'EXIBICAO']}
				label={pageFieldsData.label}
				type={pageFieldsData.type}
				className={className}
				onChange={handleChange}
				onFocus={focusCallback}
				hasFeedback
				validateStatus={loading ? 'validating' : error ? 'error' : 'success'}
				validateTrigger={loading ? 'onBlur' : 'onChange'}
				rules={
					visible && [
						{
							required: !pageFieldsData.optional,
							message: 'Este campo é obrigatório.',
						},
					]
				}
				colon={false}
				help={loading ? 'Carregando...' : error && 'Não encontrado'}
				initialValue={
					typeof inputValue === 'object' ? inputValue.exibicao : ''
				}>
				<Input autoFocus={first} disabled={disabled} />
			</Form.Item>

			<Form.Item
				style={{ display: 'none' }}
				key={'INDICE ' + name}
				name={[name, 'INDICE']}
				rules={[
					{
						required: !pageFieldsData.optional,
						message: 'Este campo é obrigatório.',
					},
				]}
				initialValue={typeof inputValue === 'object' ? inputValue.indice : ''}>
				<Input />
			</Form.Item>
		</>
	)
}

const AutocompletePropTypes = {
	inputValue: object,
	form: object,
	name: string,
	onChange: func,
	waitTimeout: object,
	disabled: bool,
	dispatch: func,
	pageFieldsData: object,
	first: bool,
	className: string,
	focusCallback: func,
	visible: bool,
	state: object,
}

WithAutocomplete.propTypes = AutocompletePropTypes

WithoutAutocomplete.propTypes = AutocompletePropTypes
